/**
 * Single source of truth for the app's colors, in both themes.
 *
 * These are the only place real hex values live. They are consumed in two
 * ways, both kept in sync automatically:
 *   1. As CSS custom properties applied to <html> at runtime
 *      (see themeModeContext) — read by `palette` (inline styles) and SCSS.
 *   2. Directly, to build the Material UI theme for the active mode.
 */

export type ThemeMode = 'dark' | 'light';

/** Every color token in the design system. Keys map 1:1 to CSS variables. */
export interface ThemeColors {
  // Backgrounds
  bgApp: string; // page background
  bgPrimary: string; // cards / primary surfaces
  bgSurface: string; // inputs, menus, popovers
  bgElevated: string;
  bgDark: string;
  bgTableAlt: string; // alternate table row / footer

  // Primary accent — gold
  accent: string;
  accentDark: string;
  accentText: string;

  // Secondary accent — teal
  teal: string;
  tealText: string;

  // Text
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textDark: string;

  // Bet outcomes
  betWrong: string;
  betCorrect: string;
  betPerfect: string;

  // Actions / misc
  link: string;
  successAccept: string;
  errorReject: string;
  border: string;
  scoreInputBg: string;
  hoverBorder: string;
  tabIndicator: string;
}

// Dark theme — deep "stadium night" navy with gold/teal accents.
// Tuned so text/background pairs clear WCAG AA.
export const darkColors: ThemeColors = {
  bgApp: '#0A1730',
  bgPrimary: '#14213F',
  bgSurface: '#243352',
  bgElevated: '#1E2C49',
  bgDark: '#16243C',
  bgTableAlt: '#1B2A47',

  accent: '#F9C04C',
  accentDark: '#D9A02C',
  accentText: '#10141C',

  teal: '#34D1C1',
  tealText: '#06203A',

  textPrimary: '#F4F7FB',
  textSecondary: '#9FB0C3',
  textMuted: '#B4BFCE',
  textDark: '#111111',

  betWrong: '#FF5365',
  betCorrect: '#57D27C',
  betPerfect: '#34E36B',

  link: '#5BA8FF',
  successAccept: '#7BE38F',
  errorReject: '#E54D4D',
  border: '#32415E',
  scoreInputBg: '#E8F0EE',
  hoverBorder: '#F4F7FB',
  tabIndicator: '#5BA8FF',
};

// Light theme — clean white/cool-grey surfaces with dark navy text.
// Accents kept as fills with dark text; text colors all clear WCAG AA on white.
export const lightColors: ThemeColors = {
  bgApp: '#F1F4F9',
  bgPrimary: '#FFFFFF',
  bgSurface: '#EAEFF6',
  bgElevated: '#E2E9F2',
  bgDark: '#D6DEEA',
  bgTableAlt: '#EDF1F7',

  accent: '#E0A11C',
  accentDark: '#C2820F',
  accentText: '#1A1A1A',

  // Azure blue — used as the active-tab / "Bet" button fill in light mode.
  teal: '#1E88D5',
  tealText: '#06203A',

  textPrimary: '#15223A',
  textSecondary: '#4C5C72',
  textMuted: '#586676',
  textDark: '#111111',

  betWrong: '#C62737',
  betCorrect: '#1E9D4C',
  betPerfect: '#0E7C39',

  link: '#1565C0',
  successAccept: '#1E9D4C',
  errorReject: '#C84A43',
  border: '#CFD8E4',
  scoreInputBg: '#E8F0EE',
  hoverBorder: '#15223A',
  tabIndicator: '#1565C0',
};

export const colorsByMode: Record<ThemeMode, ThemeColors> = {
  dark: darkColors,
  light: lightColors,
};

/** camelCase token key → CSS custom property name (e.g. bgPrimary → --bg-primary). */
export function cssVarName(token: keyof ThemeColors): string {
  return '--' + token.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase());
}

/** Writes every color token for `mode` onto the document root as CSS variables. */
export function applyThemeVariables(mode: ThemeMode): void {
  const colors = colorsByMode[mode];
  const root = document.documentElement;
  (Object.keys(colors) as Array<keyof ThemeColors>).forEach((token) => {
    root.style.setProperty(cssVarName(token), colors[token]);
  });
  root.setAttribute('data-theme', mode);
  root.style.colorScheme = mode;
}
